import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Badge } from 'react-bootstrap';
import useHover from '../CustomHooks/useHover';
import '../Layout/SubHeader/SubHeader.css';

/*
 * Rail glyphs are drawn on a 28px box with a 24px glyph inside it, so the SVG box is
 * 28 to land the glyph at the 24px the frame draws — the same footprint the icomoon
 * `font-size: var(--28px)` rule gave the font icons this rail used before.
 */
const MENU_ICON_SIZE = 28;

function MenuItem({
  pathName,
  linkTo,
  iconClass,
  iconUrl,
  IconComponent,
  titleKey,
  activeClassList,
  trafficLightStyle,
  aTag,
  badgeData,
  activeId,
  isMobileMenu,
  stateClass = '',
  activeExcludeList = [],
}) {
  const { t } = useTranslation('common');
  const { t: sMaster } = useTranslation('systemMaster');
  const { hover, coords, handleMouseEnter, handleMouseLeave } = useHover();
  const menuTitleStyle = (coordsParam) => ({
    position: 'fixed',
    top: `${coordsParam.top}px`,
    left: 44,
    zIndex: 999,
    opacity: 1,
  });

  /**
   * Active matching is a substring test, so a longer path that merely contains a shorter entry
   * lights that entry up too — `/product-costing-v2` matches `product-costing`. `activeExcludeList`
   * lets a caller name the paths that must NOT count as a match for this item, so two menu entries
   * whose paths share a prefix can each highlight on their own route.
   */
  const isExcluded = activeExcludeList.some((link) => pathName.includes(link));
  const isActive = !isExcluded && (activeId ? pathName.includes(activeId) : activeClassList.some((link) => pathName.includes(link)));
  const menuTitle = t(titleKey, { defaultValue: sMaster(titleKey) });
  /**
   * Draws the rail glyph. A caller-supplied image URL wins (uploaded module icons),
   * then an SVG component, then the icomoon font class. Callers already passed
   * `iconUrl` for uploaded icons but the prop was not accepted, so those icons were
   * silently dropped and every custom module fell back to the generic glyph.
   * @returns {JSX.Element} The icon element.
   */
  const renderIcon = () => {
    if (iconUrl) {
      /*
       * Uploaded icons are arbitrary aspect ratios — observed 17x17 through 1000x667
       * photographs. `cover` + the master-design 4px radius crops them to a square tile
       * so they sit on the same 28px rhythm as the SVG glyphs; `contain` letterboxed
       * landscape uploads into a thin strip. `no-referrer` matches the System Master
       * renderer so presigned S3 URLs are not rejected on the referrer check.
       */
      return (
        <img
          src={iconUrl}
          alt=""
          width={MENU_ICON_SIZE}
          height={MENU_ICON_SIZE}
          referrerPolicy="no-referrer"
          style={{ objectFit: 'cover', borderRadius: 4, display: 'block' }}
          className={trafficLightStyle || ''}
        />
      );
    }
    if (IconComponent) {
      return <IconComponent size={MENU_ICON_SIZE} className={trafficLightStyle || ''} />;
    }
    return <i className={`${iconClass} ${trafficLightStyle || ''}`} />;
  };

  const renderLinkContent = (isTitleRequired) => (
    <>
      {renderIcon()}
      {isTitleRequired && menuTitle}
      {badgeData?.count > 0 && (
        <Badge bg="secondary" className={badgeData.colour || 'amber'}>
          {badgeData?.count}
        </Badge>
      )}
    </>
  );

  let linkElement;

  if (aTag && isMobileMenu) {
    linkElement = (
      <a aria-label="a tag" className={`btn-subnav ${isActive ? 'active' : ''}`} href={linkTo}>
        {renderLinkContent(true)}
      </a>
    );
  } else if (aTag) {
    linkElement = (
      <a onMouseEnter={handleMouseEnter} aria-label="a tag" onMouseLeave={handleMouseLeave} className="btn-subnav" href={linkTo}>
        {renderIcon()}
      </a>
    );
  } else if (isMobileMenu) {
    linkElement = (
      <Link to={linkTo} className={`btn-subnav ${isActive ? 'active' : ''}`}>
        {renderLinkContent(true)}
      </Link>
    );
  } else {
    linkElement = (
      <Link onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} to={linkTo} className={`btn-subnav ${isActive ? 'active' : ''}`}>
        {renderLinkContent()}
      </Link>
    );
  }

  return (
    <li className={`${isActive ? 'active' : ''} ${stateClass}`.trim()}>
      {linkElement}
      {hover && (
        <span className={`${trafficLightStyle} hover-title`} style={menuTitleStyle(coords)}>
          {menuTitle}
        </span>
      )}
    </li>
  );
}

MenuItem.defaultProps = { IconComponent: null };

export default MenuItem;
